/**
 * BigQuery export history, via /api/bq-jobs.
 *
 * Reading it needs the Google service-account key, which is server-side only
 * (googleServiceAccountAuth.ts uses node:crypto and must never reach the
 * browser), so this goes through the endpoint rather than talking to BigQuery
 * directly, sending the caller's master session token.
 *
 * @module services/bigQueryJobService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

export interface BigQueryJob {
  jobId: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  state: string;
  /** "reason: message" when the load failed, null when it succeeded. */
  error: string | null;
  bytesProcessed: number | null;
}

export interface BigQueryJobLog {
  /** false when BQ_PROJECT / the service-account key are unset — not an error. */
  configured: boolean;
  /** false when BQ_EXPORT_ENABLED is not "true": this deployment reads the history but never exports. */
  exportEnabled: boolean;
  jobs: BigQueryJob[];
  /**
   * When the sync last wrote the source table. Newer than the newest export means
   * the warehouse is behind — the only thing that fires an export is the sync
   * worker draining its queue.
   */
  lastSyncAt: string | null;
  /** Whether the worker creates ws_<slug> datasets itself, or a human runs the CLI. */
  autoProvision: boolean;
  /** Workspaces with data but no ws_<slug> dataset, checked while autoProvision is off; null = not checked or could not tell. */
  unprovisioned: Array<{ name: string; dataset: string }> | null;
  retentionDays: number | null;
  target: { project: string; dataset: string; table: string; location: string } | null;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const listBigQueryJobs = async (): Promise<ServiceResult<BigQueryJobLog>> => {
  try {
    const { data: session } = await getSupabaseClient().auth.getSession();
    const token = session.session?.access_token;
    if (!token) return { success: false, error: "Not signed in" };

    const resp = await fetch("/api/bq-jobs", {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await resp.json().catch(() => null)) as (Partial<BigQueryJobLog> & { error?: string }) | null;
    if (!resp.ok) return { success: false, error: data?.error ?? "Failed to load BigQuery job history" };

    return {
      success: true,
      data: {
        configured: data?.configured === true,
        exportEnabled: data?.exportEnabled === true,
        jobs: data?.jobs ?? [],
        lastSyncAt: data?.lastSyncAt ?? null,
        autoProvision: data?.autoProvision === true,
        unprovisioned: Array.isArray(data?.unprovisioned) ? data.unprovisioned : null,
        retentionDays: data?.retentionDays ?? null,
        target: data?.target ?? null,
      },
    };
  } catch (e) {
    logError("BigQueryJob", "list failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load BigQuery job history" };
  }
};

/** What a manual export moved, so the UI can report it rather than say "done". */
export interface BigQueryExportRun {
  rowsRead: number;
  rowsLoaded: number;
  jobId: string;
}

/**
 * Fire the export by hand — the emergency handle for a stopped sync worker.
 *
 * The export only ever fired from the worker, so a stopped worker froze the
 * warehouse with no remedy inside the app: BigQuery sat eleven days behind and
 * the only fix was shell access. This calls the same function the worker calls.
 *
 * Slow on purpose to admit: a full reload is seconds to tens of seconds, and
 * the caller must keep the button disabled for the whole of it rather than let
 * someone queue three truncating loads over each other.
 */
export const runBigQueryExport = async (): Promise<ServiceResult<BigQueryExportRun>> => {
  try {
    const { data: session } = await getSupabaseClient().auth.getSession();
    const token = session.session?.access_token;
    if (!token) return { success: false, error: "Not signed in" };

    const resp = await fetch("/api/bq-export-run", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await resp.json().catch(() => null)) as
      | (Partial<BigQueryExportRun> & { error?: string })
      | null;
    if (!resp.ok) return { success: false, error: data?.error ?? "Export failed" };

    return {
      success: true,
      data: {
        rowsRead: data?.rowsRead ?? 0,
        rowsLoaded: data?.rowsLoaded ?? 0,
        jobId: data?.jobId ?? "",
      },
    };
  } catch (e) {
    logError("BigQueryJob", "manual export failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Export failed" };
  }
};
